import { Link } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent } from "@/components/ui/card";

function FeeBreakdown() {
  return (
    <Card>
      <CardContent className="p-5 space-y-2 text-sm">
        <div className="flex justify-between">
          <span>Donation</span>
          <span className="font-mono tabular-nums">$50.00</span>
        </div>
        <div className="flex justify-between text-muted-foreground items-center">
          <span className="flex items-center gap-2">
            <span className="h-3.5 w-3.5 rounded border border-primary bg-primary/20" />
            Cover the processing fee
          </span>
          <span className="font-mono tabular-nums">+ $1.43</span>
        </div>
        <div className="border-t border-border pt-2 flex justify-between font-medium">
          <span>You'll be charged</span>
          <span className="font-mono tabular-nums">$51.43</span>
        </div>
        <div className="flex justify-between text-success">
          <span>School receives</span>
          <span className="font-mono tabular-nums">$50.00</span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function FeesAndPricing() {
  return (
    <>
      <p>
        PagePledge charges a flat fee per campaign — never a percentage of what you raise. Square charges its usual card-processing fee on each donation. That's it. No subscription, no setup costs, no per-student charge. Here's how the numbers work.
      </p>

      <h2>What PagePledge charges</h2>
      <p>
        An <strong>early adopter discount</strong> is available on your school’s flat campaign fee. Prices, with the discount already taken off, are on the <a href="/pricing">pricing page</a>, so you know exactly what you’ll pay. You start with a free trial, so you can set up your campaign and see how PagePledge works. Running the campaign (going live, taking donations, adding teachers and emailing families) needs the fee paid first: pay by e-Transfer and we’ll approve it. The setup guide shows the price based on how many students you’ve added. See <Link href="/help/articles/paying-for-your-campaign">Paying for your campaign</Link>.
      </p>
      <Callout variant="note" title="Why flat, not a percentage?">
        <p>
          A percentage punishes schools that fundraise well. A school that raises $20,000 shouldn't have to pay four times more than a school that raises $5,000 — they're using the same tool. The more you raise, the more your school keeps.
        </p>
      </Callout>

      <h2>What Square charges</h2>
      <p>
        Square takes a small fee on every donation — about <strong>2.8% + $0.30 CAD</strong> for online card donations. That goes to Square, not us, and it comes out before the money lands in your bank. The current rate is at <a href="https://squareup.com/ca/en/pricing" target="_blank" rel="noopener noreferrer">squareup.com/ca/en/pricing</a> if you want to double-check.
      </p>

      <h2>Donors usually cover the fee</h2>
      <p>
        At checkout, donors see a checkbox to cover that processing fee themselves — it's checked by default. When checked, the donor pays an extra dollar or two and your school gets the full pledged amount. When unchecked, your school still gets the donation, just with the fee taken off.
      </p>

      <Screenshot caption="What the donor sees at checkout">
        <div className="max-w-md mx-auto">
          <FeeBreakdown />
        </div>
      </Screenshot>

      <p>
        Most donors leave it checked — about 80% in the schools we've watched. An extra dollar to know 100% of their gift reaches the students is an easy yes.
      </p>

      <h2>Optional extra contributions</h2>
      <p>
        If checkout offers an optional extra contribution, its recipient and amount are shown before payment. Extra contributions are separate from your school’s campaign fee.
      </p>

      <h2>Canadian dollars only</h2>
      <p>We're Canadian and our schools are Canadian, so for now donations are in CAD only. We'll add other currencies if and when we head past the border.</p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/tracking-donations">Tracking donations</Link>.</li>
        <li><Link href="/help/articles/connecting-square">Connecting Square</Link>.</li>
      </ul>
    </>
  );
}
